<script setup>
useSeoMeta({
  title: "VimiCrypto - The best crypto portfolio tracker",
  ogTitle: "VimiCrypto - The best crypto portfolio tracker",
  description:
    "Track your crypto portfolio with ease. Get insights and analytics on your investments.",
  ogDescription:
    "Track your crypto portfolio with ease. Get insights and analytics on your investments.",
  ogImage: "https://vimicrypto.com/images/logo.svg",
  twitterImage: "https://vimicrypto.com/images/logo.svg",
  twitterCard: "logo",
  ogUrl: "https://vimicrypto.com",
  twitterTitle: "VimiCrypto - The best crypto portfolio tracker",
  twitterDescription:
    "Track your crypto portfolio with ease. Get insights and analytics on your investments.",
});

const router = useRouter();
const user = useCurrentUser();

useHead({
  htmlAttrs: {
    lang: "en",
  },
  link: [
    {
      rel: "icon",
      type: "image/x-icon",
      href: "/favicon.ico",
    },
  ],
  script: [
    {
      children: `(function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "nk9g0cih7q");`,
    },
    {
      children: `clarity("set", "user", "${user.value?.email}")`,
    },
  ],
});

onMounted(() => {
  watch(user, (user, prevUser) => {
    if (prevUser && !user) {
      // user logged out
      router.push("/");
    }
  });
});

// Load fiat data globally for currency conversion across all routes - only when user is authenticated
const fiatStore = useFiatStore();

// Only load fiat data if user is signed in and we can get API headers
// NOTE: Removed top-level await to prevent memory accumulation during app initialization
if (user.value) {
  // Load API headers and fetch fiat data asynchronously without blocking
  useApiHeaders(true).then(apiHeaders => {
    const fiatFetchOptions = {
      immediate: true,
      headers: apiHeaders.headers,
      onResponse({ response }) {
        if (response._data && Array.isArray(response._data)) {
          const processedFiats = response._data.map((fiat) => ({
            date: fiat.date,
            base: fiat.base,
            rates: fiat.rates,
          }));
          fiatStore.setFiats(processedFiats);
          console.log('Global fiat data loaded successfully');
        }
      },
      onResponseError({ error }) {
        console.error('Global fiat loading failed:', error);
      }
    };

    // Non-blocking fiat data fetch
    useFetch("/api/trade/fiat", fiatFetchOptions);
  }).catch(error => {
    console.warn('Could not get API headers for fiat loading:', error);
  });
}

// Watch for user authentication changes and load fiat data when user signs in
watch(user, async (currentUser, prevUser) => {
  if (currentUser && !prevUser && !fiatStore.fiats) {
    // User just signed in, load fiat data
    try {
      const apiHeaders = await useApiHeaders(true);
      const fiatResponse = await $fetch("/api/trade/fiat", apiHeaders);
      
      if (fiatResponse && Array.isArray(fiatResponse)) {
        const processedFiats = fiatResponse.map((fiat) => ({
          date: fiat.date,
          base: fiat.base,
          rates: fiat.rates,
        }));
        fiatStore.setFiats(processedFiats);
        console.log('Fiat data loaded after user authentication');
      }
    } catch (error) {
      console.error('Failed to load fiat data after authentication:', error);
    }
  }
});

// Note: Coin loading moved to system/index.vue since coins are only needed in system pages
// This prevents blocking the entire app if coin loading fails

const { isLoading } = useLoadingIndicator();
</script>

<template>
  <UApp>
    <NuxtLoadingIndicator />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </UApp>
</template>
